/**
 * LiveSessionDetailPage.jsx — premium "Diplomexa Live Classroom" page.
 *
 * Combines:
 *   • cinematic hero
 *   • Join Live Classroom flow (sandbox-aware)
 *   • Trainer Control Panel (start/stop recording, attendance, premium tools)
 *   • Recording Player (state machine: not_started/recording/processing/ready/failed)
 *   • Attendance Summary card
 *   • Premium Diploks tools section
 *   • Feedback form
 *
 * Provider name NEVER appears in the UI.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import { toast } from "sonner";
import {
  Radio, ArrowLeft, Play, Square, Loader2, Sparkles, Users,
  Mic2, ScrollText, FileSpreadsheet, Crown, Heart, Star,
  Lock, Clock, CheckCircle2, AlertCircle, Send, Award,
  Video, Headphones,
} from "lucide-react";
import {
  getLiveSession, joinLiveSession, leaveLiveSession,
  startRecording as apiStartRec, stopRecording as apiStopRec,
  getRecordingStatus, getRecordingPlayback, submitFeedback,
  getAttendance, premiumLiveSummary, premiumTrainerReport,
  premiumParentReport, premiumAttendanceCert, premiumSessionNotes,
  formatApiError,
} from "../../lib/liveClassApi";
import { requestDiploksConsent } from "../../components/DiploksConsentModal";
import { showLowBalance } from "../../components/DiploksLowBalanceDialog";
import AskDiplomexaLiveOverlay from "../../components/lms-live/AskDiplomexaLiveOverlay";
import LiveNoteHistoryPanel from "../../components/lms-live/LiveNoteHistoryPanel";
import LivePollsQuizPanel from "../../components/lms-live/LivePollsQuizPanel";
import { DiplomexaIntelligenceOutputRenderer } from "../../components/diplomexa-intelligence/DiplomexaIntelligenceOutput";
import { useAuth } from "../../context/AuthContext";

// Premium-tool catalogue.
// `title` + `emphasis` flow into the Diplomexa Intelligence renderer
// header (serif title + italic-gold emphasis). `cost` lets the renderer
// surface "Diploks used" in the export toolbar.
const PREMIUM_TOOLS = [
  { key: "live_ai_session_summary",   icon: Sparkles,    label: "AI Session Summary",
    title: "Session Summary",         emphasis: "overview",   cost: 8,
    desc: "Diplomexa Intelligence summary of what was covered, engagement & next steps.",
    run: premiumLiveSummary, audience: "all" },
  { key: "live_session_notes_export", icon: FileSpreadsheet, label: "Session Notes Export",
    title: "Session Notes",           emphasis: "export",     cost: 5,
    desc: "Tidy student-facing notes with action items & a prep question.",
    run: premiumSessionNotes, audience: "all" },
  { key: "live_attendance_certificate", icon: Award,    label: "Attendance Certificate",
    title: "Attendance Certificate",  emphasis: "issued",     cost: 6,
    desc: "Personalised certificate for your live attendance.",
    run: premiumAttendanceCert, audience: "learner" },
  { key: "live_parent_attendance_report", icon: Heart,  label: "Parent Attendance Report",
    title: "Parent Attendance",       emphasis: "report",     cost: 7,
    desc: "Calm, supportive note for parents on attendance + progress.",
    run: premiumParentReport, audience: "parent" },
  { key: "live_trainer_feedback_report",  icon: Crown,  label: "Trainer Feedback Report",
    title: "Trainer Feedback",        emphasis: "report",     cost: 10,
    desc: "Coach-style report for trainers — engagement, gaps & next plan.",
    run: premiumTrainerReport, audience: "host" },
];

export default function LiveSessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "delegate";
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinCtx, setJoinCtx] = useState(null);
  const [recording, setRecording] = useState(null);
  const [playback, setPlayback] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [premiumOut, setPremiumOut] = useState(null);
  const [feedback, setFeedback] = useState({ rating: 5, comment: "" });

  const reload = async () => {
    try {
      const data = await getLiveSession(sessionId);
      setS(data);
      setRecording(data.recording);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [sessionId]);

  // Poll recording status every 7s while a recording is in flight.
  useEffect(() => {
    if (!recording) return;
    if (!["recording", "processing"].includes(recording.status)) return;
    const t = setInterval(async () => {
      try {
        const r = await getRecordingStatus(sessionId);
        setRecording(r);
      } catch { /* silent */ }
    }, 7000);
    return () => clearInterval(t);
  }, [sessionId, recording?.status, recording]);

  const onJoin = async () => {
    setJoining(true);
    try {
      const out = await joinLiveSession(sessionId);
      setJoinCtx(out.join_ticket);
      toast.success(`Joining classroom as ${out.join_ticket.role}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setJoining(false); }
  };

  const onLeave = async () => {
    try {
      await leaveLiveSession(sessionId);
      setJoinCtx(null);
      toast.message("Left classroom");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onStartRec = async () => {
    try {
      const r = await apiStartRec(sessionId);
      setRecording(r);
      toast.success("Recording started");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onStopRec = async () => {
    try {
      await apiStopRec(sessionId);
      toast.success("Recording stopped — processing now");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFetchPlayback = async () => {
    try {
      const r = await getRecordingPlayback(sessionId);
      if (!r.available) {
        toast.warning(r.reason || "Recording not ready");
        return;
      }
      setPlayback(r);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onLoadAttendance = async () => {
    try {
      setAttendance(await getAttendance(sessionId));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onSubmitFeedback = async () => {
    try {
      await submitFeedback(sessionId, feedback);
      toast.success("Thanks for the feedback!");
      setFeedback({ rating: 5, comment: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runPremium = async (tool) => {
    setPremiumOut({ loading: true, key: tool.key });
    try {
      // Pre-flight consent
      try {
        const r = await (await import("../../lib/api")).default.get(
          "/diploks/usage-consent/status");
        if (r.data?.needs_consent) {
          const ok = await requestDiploksConsent();
          if (!ok?.ok) { setPremiumOut(null); return; }
        }
      } catch { /* fall through */ }
      const data = await tool.run(sessionId);
      if (!data.ok) {
        // shouldn't happen — backend raises 4xx — but be defensive
        toast.error(data.consume?.reason || "Could not run tool");
        setPremiumOut(null);
        return;
      }
      window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
      toast.success(`${data.consume?.cost} Diploks used`, {
        description: `Balance: ${data.consume?.balance_after}`,
      });
      setPremiumOut({ tool, output: data.output });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        showLowBalance({ feature_key: tool.key, feature_label: tool.label });
      } else if (status === 409) {
        const ok = await requestDiploksConsent();
        if (ok?.ok) return runPremium(tool);
      } else if (status === 403) {
        toast.error("This tool isn't available for your role on this session.");
      } else {
        toast.error(formatApiError(e));
      }
      setPremiumOut(null);
    }
  };

  const startsIn = useMemo(() => {
    if (!s) return null;
    const ms = new Date(s.start_time) - new Date();
    if (ms <= 0) return null;
    const min = Math.round(ms / 60000);
    if (min < 60) return `Starts in ${min} min`;
    return `Starts in ${Math.round(min / 60)} h ${min % 60} min`;
  }, [s]);

  if (loading || !s) {
    return (
      <DashboardLayout>
        <div className="grid h-72 place-items-center"><LoadingSkeleton variant="page" label="Entering the classroom…" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Live Classroom">
      <div data-testid="lmslive-detail" className="space-y-6 pb-12">
        {/* Cockpit hero — Bloomberg-terminal density */}
        <section
          data-testid="lmslive-detail-hero"
          className="relative overflow-hidden rounded-sm border border-brand-gold/20 bg-brand-navy-dark text-brand-champagne shadow-editorial"
        >
          {/* Foil-stripe */}
          <div aria-hidden className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
          <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
          <div aria-hidden className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-brand-gold/12 blur-3xl" />

          {/* Status strip — Bloomberg meta bar */}
          <div className="relative flex flex-wrap items-center justify-between gap-3 border-b border-brand-gold/15 px-5 py-2.5">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-champagne/55">
              <button
                onClick={() => navigate("/dashboard/lms/live-sessions")}
                data-testid="lmslive-detail-back"
                className="inline-flex items-center gap-1 hover:text-brand-gold transition-colors"
              >
                <ArrowLeft size={11} /> SESSIONS
              </button>
              <span className="opacity-40">·</span>
              <span>LIVE CLASSROOM</span>
              <span className="opacity-40">·</span>
              <span>{new Date().toUTCString().slice(17, 25)} UTC</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-bold ring-1 ${
                s.status === "live"
                  ? "bg-rose-500/15 ring-rose-400/40 text-rose-300"
                  : s.status === "ended"
                  ? "bg-emerald-500/15 ring-emerald-400/40 text-emerald-300"
                  : "bg-brand-gold/15 ring-brand-gold/40 text-brand-gold"
              }`}>
                {s.status === "live" ? <Radio size={10} className="animate-pulse" /> : null}
                {s.status}
              </span>
              {s.is_host ? (
                <span className="rounded-sm bg-brand-gold px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.22em] text-brand-navy-dark">
                  Host
                </span>
              ) : null}
            </div>
          </div>

          <div className="relative p-6 md:p-8">
            <p className="eyebrow text-brand-gold flex items-center gap-2">
              <Radio size={11} /> Diplomexa Live Classroom
            </p>
            <h1 className="mt-3 font-display text-3xl md:text-5xl leading-tight text-brand-champagne">
              {s.title}
            </h1>
            <div className="divider-gold-hairline mt-6 max-w-md" />
            {s.description ? (
              <p className="mt-5 max-w-3xl text-sm text-brand-champagne/65 leading-relaxed">
                {s.description}
              </p>
            ) : null}

            {/* Bloomberg-style data row — start · duration · enrolled · cohort */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 max-w-3xl">
              <DataCell
                label="Start"
                value={new Date(s.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                sub={new Date(s.start_time).toLocaleDateString([], { month: "short", day: "numeric" })}
              />
              <DataCell
                label="Until"
                value={new Date(s.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                sub={new Date(s.end_time).toLocaleDateString([], { month: "short", day: "numeric" })}
              />
              <DataCell
                label="Enrolled"
                value={(s.enrolled_user_ids || []).length}
                sub="delegates"
              />
              <DataCell
                label="Starts in"
                value={startsIn || "—"}
                sub={s.status === "live" ? "now live" : "countdown"}
                highlight={!!startsIn}
              />
            </div>

            <div className="mt-7 flex flex-wrap gap-2">
              {s.can_join && !joinCtx ? (
                <button
                  type="button"
                  onClick={onJoin}
                  disabled={joining}
                  data-testid="lmslive-join-btn"
                  className="inline-flex items-center gap-2 rounded-sm bg-brand-gold px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-navy-dark hover:bg-brand-gold-light shadow-gold-soft hover:shadow-gold-strong disabled:opacity-50 transition-all duration-300"
                >
                  {joining ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />}
                  Join Live Classroom
                </button>
              ) : null}
              {joinCtx ? (
                <button
                  type="button"
                  onClick={onLeave}
                  data-testid="lmslive-leave-btn"
                  className="inline-flex items-center gap-2 rounded-sm bg-rose-500 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-rose-600 transition-colors"
                >
                  Leave
                </button>
              ) : null}
              {!s.can_join && !s.is_host ? (
                <span className="inline-flex items-center gap-2 rounded-sm bg-white/5 border border-brand-gold/20 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-champagne/70">
                  <Lock size={12} /> Enrollment required
                </span>
              ) : null}
            </div>
          </div>
        </section>

        {/* Provider-agnostic "stage" — opaque join ticket only */}
        {joinCtx ? (
          <ClassroomStage joinCtx={joinCtx} sessionTitle={s.title} />
        ) : null}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Main column */}
          <main className="lg:col-span-8 space-y-5">
            {s.is_host ? (
              <TrainerControlPanel
                session={s}
                recording={recording}
                onStartRec={onStartRec}
                onStopRec={onStopRec}
              />
            ) : null}

            <RecordingCard
              recording={recording}
              playback={playback}
              onFetchPlayback={onFetchPlayback}
              sessionTitle={s.title}
            />

            <PremiumToolsCard
              session={s}
              tools={PREMIUM_TOOLS}
              onRun={runPremium}
              output={premiumOut}
              onCloseOutput={() => setPremiumOut(null)}
            />

            {!s.is_host ? (
              <FeedbackCard
                feedback={feedback} setFeedback={setFeedback}
                onSubmit={onSubmitFeedback}
              />
            ) : null}
          </main>

          {/* Side rail */}
          <aside className="lg:col-span-4 space-y-5">
            <AttendanceCard
              session={s}
              attendance={attendance}
              onLoad={onLoadAttendance}
            />
            <PolicyCard />
          </aside>
        </div>
        {/* Persistent thread of all my live notes for this session. */}
        <LiveNoteHistoryPanel
          sessionId={sessionId}
          trainerView={["super_admin", "org_admin", "event_manager",
                          "chair", "faculty_advisor"].includes(role)}
        />
        {/* Polls + Quizzes inside Live Classroom (Sprint LMS-Live Feb '26) */}
        <div className="mt-8">
          <LivePollsQuizPanel
            sessionId={sessionId}
            isHost={!!s?.is_host}
          />
        </div>
      </div>
      {/* "Ask Diplomexa Live" floating overlay — only while joined. */}
      {joinCtx ? (
        <AskDiplomexaLiveOverlay
          sessionId={sessionId}
          sessionTitle={s?.title}
        />
      ) : null}
    </DashboardLayout>
  );
}

// ============================================================================
// Sub-components
// ============================================================================
/**
 * DataCell — Bloomberg-terminal style key/value tile.
 * Eyebrow label · large monospace value · small slate sub-line.
 */
function DataCell({ label, value, sub, highlight = false }) {
  return (
    <div className="space-y-1.5">
      <p className="eyebrow text-brand-champagne/40">{label}</p>
      <p className={`font-mono text-2xl leading-none ${highlight ? "text-brand-gold" : "text-brand-champagne"}`}>
        {value}
      </p>
      {sub ? (
        <p className="text-[10px] uppercase tracking-[0.22em] text-brand-champagne/40">
          {sub}
        </p>
      ) : null}
    </div>
  );
}



function ClassroomStage({ joinCtx, sessionTitle }) {
  // Sandbox mode shows a faux stage; real provider mode would mount the
  // 100ms Prebuilt iframe — used when the backend returned a `prebuilt_url`.
  // Falls back to the legacy SDK placeholder if absent (mock provider, older
  // sessions, opt-out, etc.).  Sandbox flags are surfaced via the sub-header.
  const prebuiltUrl = joinCtx?.prebuilt_url;

  return (
    <section
      data-testid="lmslive-classroom-stage"
      className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A192F] to-[#0F2C50] shadow-2xl"
    >
      {/* Top chrome — branded, provider-agnostic */}
      <div className="flex items-center justify-between px-4 py-2 text-[11px] text-blue-100">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            <Radio size={10} /> {joinCtx.role}
          </span>
          <span className="font-semibold text-white">{sessionTitle}</span>
        </div>
        <span className="text-blue-200">
          Diplomexa Live · token expires {new Date(joinCtx.expires_at).toLocaleTimeString()}
        </span>
      </div>

      {prebuiltUrl ? (
        <div className="relative aspect-video bg-black">
          <iframe
            data-testid="lmslive-prebuilt-iframe"
            title="Diplomexa Live Classroom"
            src={prebuiltUrl}
            allow="camera; microphone; display-capture; autoplay; clipboard-write; fullscreen *"
            allowFullScreen
            referrerPolicy="origin"
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
      ) : (
        <div className="aspect-video relative bg-black/70">
          <div className="absolute inset-0 flex items-center justify-center text-center">
            <div>
              <div className="grid h-16 w-16 mx-auto place-items-center rounded-2xl bg-[#D4AF37] text-[#0A192F]">
                <Headphones size={28} />
              </div>
              <p className="mt-4 text-sm font-bold uppercase tracking-[0.24em] text-[#D4AF37]">
                You&apos;re in the classroom
              </p>
              <p className="mt-1 text-lg font-semibold text-white">{sessionTitle}</p>
              <p className="mt-3 text-xs text-blue-200 max-w-md">
                {joinCtx.sandbox
                  ? "Sandbox classroom — live video stage will activate once your organisation enables the live-video provider."
                  : "Live audio/video stage active. Your session is being recorded for academy review."}
              </p>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 px-4 py-3 flex items-center justify-between text-[11px] text-blue-100 bg-gradient-to-t from-black/60 to-transparent">
            <span>Token expires {new Date(joinCtx.expires_at).toLocaleTimeString()}</span>
            <span>Diplomexa Academy · Live Stage</span>
          </div>
        </div>
      )}
    </section>
  );
}

function TrainerControlPanel({ session, recording, onStartRec, onStopRec }) {
  const isRecording = recording?.status === "recording";
  return (
    <section
      data-testid="lmslive-trainer-panel"
      className="rounded-2xl border border-[#D4AF37]/30 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
          <Crown size={14} className="text-[#D4AF37]" /> Trainer Control Panel
        </h2>
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Host: {session.is_host ? "You" : "—"}
        </span>
      </div>
          <p className="mt-2 text-xs text-slate-500">
            {session.recording_enabled
              ? "Recording is enabled for this session. Auto-record starts when you join."
              : "Recording is disabled for this session."}
          </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onStartRec}
          disabled={!session.recording_enabled || isRecording}
          data-testid="lmslive-rec-start"
          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-500 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-rose-600 disabled:opacity-50"
        >
          <Play size={11} /> Start Recording
        </button>
        <button
          type="button"
          onClick={onStopRec}
          disabled={!isRecording}
          data-testid="lmslive-rec-stop"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A192F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50"
        >
          <Square size={11} /> Stop Recording
        </button>
      </div>
    </section>
  );
}

function RecordingCard({ recording, playback, onFetchPlayback, sessionTitle }) {
  const status = recording?.status || "not_started";
  const labels = {
    not_started: "No recording yet",
    recording:   "Recording in progress",
    processing:  "Processing — usually 1-2 minutes after stop",
    ready:       "Recording available",
    failed:      "Recording failed — contact support",
  };
  const dots = {
    not_started: "bg-slate-300",
    recording:   "bg-rose-500 animate-pulse",
    processing:  "bg-amber-500 animate-pulse",
    ready:       "bg-emerald-500",
    failed:      "bg-rose-700",
  };
  return (
    <section
      data-testid={`lmslive-recording-${status}`}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
          <Video size={14} className="text-[#D4AF37]" /> Recording
        </h2>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0A192F]">
          <span className={`h-2 w-2 rounded-full ${dots[status]}`} /> {labels[status]}
        </span>
      </div>

      {status === "ready" && !playback ? (
        <button
          type="button"
          onClick={onFetchPlayback}
          data-testid="lmslive-recording-load"
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#D4AF37] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30]"
        >
          <Play size={11} /> Watch recording
        </button>
      ) : null}

      {playback?.url ? (
        <div className="mt-4">
          <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
            <video
              src={playback.url.startsWith("http") ? playback.url :
                    `${process.env.REACT_APP_BACKEND_URL || ""}/api${playback.url.replace(/^\/api/, "")}`}
              controls
              controlsList={playback.download_allowed ? undefined : "nodownload"}
              onContextMenu={(e) => playback.download_allowed ? null : e.preventDefault()}
              className="w-full h-full"
              data-testid="lmslive-recording-player"
            />
            {playback.watermark ? (
              <>
                <div className="pointer-events-none absolute top-2 right-2 px-2 py-0.5 rounded bg-black/40 text-white text-[10px] tracking-widest uppercase font-semibold">
                  Diplomexa Academy
                </div>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.07]">
                  <span className="-rotate-12 text-white text-3xl font-black uppercase tracking-[0.2em]">{sessionTitle}</span>
                </div>
              </>
            ) : null}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Signed playback URL · expires in ~{Math.round((playback.expires_in_seconds || 3600) / 60)} min.
            Refresh the page if it expires.
          </p>
        </div>
      ) : null}

      {status === "processing" ? (
        <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-slate-500">
          <Loader2 size={12} className="animate-spin" /> Mock provider takes ~30 seconds in sandbox.
        </div>
      ) : null}

      {recording?.duration_seconds ? (
        <p className="mt-3 text-[11px] text-slate-500">
          Duration: {Math.round(recording.duration_seconds / 60)} min
        </p>
      ) : null}
    </section>
  );
}

function PremiumToolsCard({ session, tools, onRun, output, onCloseOutput }) {
  return (
    <section
      data-testid="lmslive-premium-tools"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
            <Sparkles size={14} className="text-[#D4AF37]" /> Premium tools · Uses Diploks
          </h2>
          <p className="mt-1 text-[11px] text-slate-500">
            Each output is branded as Diplomexa Intelligence. Diploks are deducted only on a successful run.
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
        {tools.map(t => {
          const Icon = t.icon;
          const disabled = (
            (t.audience === "host"   && !session.is_host) ||
            (t.audience === "parent" && !["parent"].includes(session?._userRole))
          );
          return (
            <button
              key={t.key}
              type="button"
              disabled={disabled}
              onClick={() => onRun(t)}
              data-testid={`lmslive-premium-${t.key}`}
              className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 text-left hover:border-[#D4AF37]/60 hover:bg-amber-50/40 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37]">
                <Icon size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-[#0A192F] truncate">{t.label}</p>
                  <span className="rounded-full bg-[#0A192F] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#D4AF37] whitespace-nowrap">
                    Premium · Uses Diploks
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] text-slate-500 line-clamp-2">{t.desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      {output?.loading ? (
        <div data-testid="lmslive-premium-loading"
              className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-center">
          <Loader2 size={20} className="mx-auto animate-spin text-[#0F4C81]" />
          <p className="mt-2 text-sm font-semibold text-[#0A192F]">
            Diplomexa Intelligence is preparing your output…
          </p>
        </div>
      ) : null}

      {output?.output ? (
        <div data-testid="lmslive-premium-output" className="mt-5 relative">
          <button
            onClick={onCloseOutput}
            aria-label="Close premium output"
            className="absolute -top-2 -right-2 z-10 grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-[11px] font-bold text-slate-400 shadow-sm hover:bg-slate-50 hover:text-slate-700"
          >
            ✕
          </button>
          <DiplomexaIntelligenceOutputRenderer
            output={output.output}
            title={output.tool?.title || output.tool?.label || "Diplomexa Intelligence"}
            emphasis={output.tool?.emphasis || "report"}
            subtitle={output.output.subtitle || ""}
            kicker={output.output.brand || "Diplomexa Intelligence"}
            icon={output.tool?.icon || Sparkles}
            diploksUsed={output.tool?.cost}
            testid="lmslive-premium-output-renderer"
          />
        </div>
      ) : null}
    </section>
  );
}

function AttendanceCard({ session, attendance, onLoad }) {
  if (!session.is_host) return null;
  return (
    <section
      data-testid="lmslive-attendance-summary"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
          <Users size={14} className="text-[#D4AF37]" /> Attendance
        </h2>
        <button
          type="button"
          onClick={onLoad}
          data-testid="lmslive-attendance-load"
          className="text-[11px] font-semibold text-[#0F4C81] hover:text-[#0A192F]"
        >
          {attendance ? "Refresh" : "Load"}
        </button>
      </div>
      {attendance ? (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] text-slate-500">
            Expected {attendance.expected_minutes} min · {attendance.total_participants} participant(s)
          </p>
          <div className="max-h-72 overflow-auto space-y-1.5">
            {(attendance.participants || []).map((p, i) => (
              <div key={p.user_id || i} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-[#0A192F] truncate">{p.user_name || p.user_id}</p>
                  <p className="text-[10px] text-slate-500">
                    {p.minutes} min · {p.segments} segment(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-[#0F4C81]">{p.attendance_pct}%</p>
                  {p.certificate_eligible ? (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase text-emerald-700">
                      <CheckCircle2 size={9} /> Cert
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-[11px] text-slate-500">
          Click <span className="font-semibold">Load</span> to view who attended and for how long.
        </p>
      )}
    </section>
  );
}

function FeedbackCard({ feedback, setFeedback, onSubmit }) {
  return (
    <section
      data-testid="lmslive-feedback"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
        <Star size={14} className="text-[#D4AF37]" /> Session feedback
      </h2>
      <div className="mt-3 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            data-testid={`lmslive-feedback-rate-${n}`}
            onClick={() => setFeedback({ ...feedback, rating: n })}
            className={`rounded-full p-1.5 transition ${
              n <= feedback.rating
                ? "bg-[#D4AF37] text-[#0A192F]"
                : "bg-slate-100 text-slate-400 hover:bg-slate-200"
            }`}
          >
            <Star size={14} />
          </button>
        ))}
      </div>
      <textarea
        value={feedback.comment}
        onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
        placeholder="Optional comment (max 1500 chars)…"
        data-testid="lmslive-feedback-comment"
        maxLength={1500}
        className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-[#0A192F]"
      />
      <button
        type="button"
        onClick={onSubmit}
        data-testid="lmslive-feedback-submit"
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#0A192F] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50]"
      >
        <Send size={11} /> Send feedback
      </button>
    </section>
  );
}

function PolicyCard() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <h3 className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0A192F] flex items-center gap-2">
        <AlertCircle size={11} /> Recording & privacy
      </h3>
      <ul className="mt-2 space-y-1 text-[11px] text-slate-600">
        <li>• Recordings are protected. No public links.</li>
        <li>• Watermarked playback. Downloads disabled by default.</li>
        <li>• Permission-checked, time-limited signed URLs.</li>
        <li>• Access is logged for audit + parent transparency.</li>
      </ul>
    </section>
  );
}
